import { DEMO_MAILTO_URL } from '../lib/links';
import heroPanel from '../assets/v2-hero-panel.jpg';
import heroSwoosh from '../assets/v2-hero-swoosh.svg';

/**
 * Hero — Figma node 1:381.
 *
 * Exact design values (at the 1920 reference width):
 *  · background  linear-gradient to right, #FFEEE1 → white at 54.896%
 *  · padding     pl-133 py-80, no right padding — the panel bleeds off-screen
 *  · text column w-762, gap-46; heading gap-24
 *  · H1          clamp(36px, 5.2vw, 60px) / 800 / 1.15 / -1px, #0F172A
 *                (2026-07 refresh: single line, replacing the two-tone
 *                "Decision Intelligence for / System Transition Governance")
 *  · subcopy     Inter 400 18px / 30px, #111827, CSS `capitalize`
 *  · CTAs        gap-32; primary #E87722 (= primary-500) px-30 py-17 r-8 w-235.757
 *  · panel       w-890 h-653, rounded-l-16, tint rgba(232,119,34,0.12),
 *                inset ring rgba(232,119,34,0.24)
 *  · screenshot  w-1136 h-619.27 at left-13 top-16, r-6, wide + tall shadow
 *
 * The panel is rendered from a SINGLE export of node 1:393 (`hero-panel.jpg`,
 * 890×653) which already contains the tint, the inner white frame, the shadow
 * and the right-hand crop of the 1136px-wide dashboard. Re-creating any of that
 * in CSS on top of the export draws the frame twice — that bug shipped once and
 * was caught in review, so keep the styling here to sizing and clipping only.
 *
 * Below `lg` the two columns stack; the panel keeps its aspect ratio and gains a
 * radius on both sides, since it is no longer bleeding off the viewport.
 */
export function Hero() {
  return (
    <section
      id="top"
      className="relative w-full overflow-hidden bg-gradient-to-r from-landing-peach to-white to-[54.896%]"
    >
      {/* Decorative line mesh (node 1:382), flipped as in the design. Never
          interactive, and hidden from assistive tech. */}
      <img
        src={heroSwoosh}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="pointer-events-none absolute bottom-[10%] left-0 hidden h-[524px] w-[1920px] max-w-none rotate-180 -scale-y-100 select-none lg:block"
      />

      <div className="relative mx-auto flex max-w-[1920px] flex-col items-start gap-12 px-5 py-14 sm:px-6 sm:py-16 md:px-8 lg:flex-row lg:items-center lg:justify-between lg:gap-8 lg:py-20 lg:pl-10 lg:pr-0 xl:pl-16 2xl:pl-[133px]">
        {/* ── Text column ── */}
        <div className="flex w-full flex-col gap-8 lg:min-w-0 lg:max-w-[762px] lg:flex-1 lg:gap-[46px]">
          <div className="flex flex-col gap-4 lg:gap-6">
            {/* SAP PartnerEdge credential — eyebrow pill above the H1.
                Peach-tinted to sit on the peach hero background without
                fighting it, and using primary-700 so the text stays readable
                once the tint drops out on the white half of the gradient. */}
            {/* <span className="inline-flex w-fit max-w-full items-center gap-2 self-start rounded-full bg-primary-500/10 px-3.5 py-1.5 text-xs font-semibold text-primary-700 sm:text-sm">
              <span
                className="size-1.5 shrink-0 rounded-full bg-primary-500"
                aria-hidden="true"
              />
              <span className="leading-snug">
                WisdomX <strong className="font-bold">Orbis</strong> is an active SAP PartnerEdge
                Open Ecosystem Build Partner
              </span>
            </span> */}

            <h1 className="m-0 text-[32px] font-bold leading-[1.2] tracking-[-0.6px] text-landing-heading sm:text-[38px] lg:text-[42px] lg:leading-[58px] lg:tracking-[-0.96px] 2xl:text-[48px] 2xl:leading-[68px]">
              Enterprise Decision Intelligence Platform
            </h1>

            {/* LP-005 — static fake chat input signalling the coming GenUI
                direction. The real POST /api/v1/chat/query endpoint is Vijay's
                next-sprint work (Section 3 of docs/WisdomX_GenUI_Brief.docx);
                this element is purely decorative until then. Marked
                aria-hidden so a screen reader does not announce it as a
                broken form field, and rendered as a plain <div> — not an
                <input> — so it can never receive focus or submit anything.
                When Vijay ships the endpoint, swap this for a real <form>
                with the input, an onSubmit posting to the endpoint, and the
                answer/panel renderer below. */}
            <div aria-hidden="true" className="flex w-full max-w-[620px] flex-col gap-2">
              <span className="text-xs font-semibold uppercase tracking-[0.08em] text-landing-grey lg:text-sm">
                Ask ORBIS anything about your estate
              </span>
              <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm lg:px-5 lg:py-4">
                <span className="text-sm italic text-slate-400 lg:text-base">
                  Which systems in our estate are costing the most and carrying the highest security
                  risk?
                  <span className="animate-caret-blink ml-0.5 inline-block h-[1em] w-[2px] translate-y-[0.15em] bg-slate-500" />
                </span>
              </div>
            </div>

            {/* LP-003 homepage introduction — verbatim from
                docs/WisdomX_GenUI_Brief.docx. Replaces the previous single
                "AI-powered … CIOs/CISOs …" paragraph. Three paragraphs, with
                the closing sentence set in a bolder navy weight so it lands
                as the mic-drop the brief writes it as. `capitalize` is dropped
                on purpose — the copy is deliberately sentence-cased. */}
            <div className="flex flex-col gap-3 text-base leading-[26px] text-landing-body sm:text-[17px] lg:gap-4 lg:text-lg lg:leading-[30px]">
              <p className="m-0">
                WisdomX <strong className="font-bold text-primary-500">ORBIS</strong> is
                purpose-built for enterprise legacy system decommissioning and cybersecurity
                intelligence.
              </p>
              <p className="m-0">
                Connect your ITSM, CMDB and ERP platforms.{' '}
                <strong className="font-bold text-primary-500">ORBIS</strong> ingests your asset
                data, applies NIST-aligned risk scoring across six domains, maps every dependency
                and vulnerability in your estate, and tells you exactly what to close, in what
                order, and what the risk of getting it wrong looks like.
              </p>
              <p className="m-0 font-semibold text-landing-heading">
                Not recommendations to review. Decisions — with the evidence behind every one.
              </p>
            </div>
          </div>

          {/* Single CTA. Figma node 2:82 ("View Product Docs") is hidden. */}
          <div className="flex w-full flex-col items-stretch sm:w-auto sm:flex-row sm:items-center">
            <a
              href={DEMO_MAILTO_URL}
              className="flex items-center justify-center rounded-lg bg-primary-500 px-[30px] py-[17px] text-lg font-bold text-white transition-colors hover:bg-primary-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700"
            >
              Request Platform Demo
            </a>
          </div>
        </div>

        {/* ── Product screenshot panel ──
            This is the ENTIRE panel (node 1:393) exported as one image, 890×653:
            the rgba(232,119,34,0.12) tint, the white rounded inner frame, the
            drop shadow and the right-hand crop of the dashboard are all part of
            the export. So NOTHING here re-creates them — doing that in CSS as
            well renders the frame twice. Only sizing and corner clipping is ours.

            xl+: exactly 890×653, flush to the viewport's right edge, rounded on
            the left only. At lg the panel is fluid but holds the 890/653 aspect,
            because pinning h-653 at a 52% width would squeeze the crop. Below lg
            it is full width and rounded on both sides — no longer a bleed.

            The text column takes the remainder rather than a second 52%: two
            52% columns plus the gap overflow the row, which pushed the panel
            71px past the viewport at 1024. */}
        <div className="w-full overflow-hidden rounded-2xl lg:aspect-[890/653] lg:w-[52%] lg:shrink-0 lg:rounded-l-2xl lg:rounded-r-none xl:aspect-auto xl:h-[653px] xl:w-[890px]">
          <img
            src={heroPanel}
            alt="Orbis Decommission Dashboard showing KPI cards, cost saving breakdown, CPU usage and risk compliance overview"
            width={890}
            height={653}
            className="block h-full w-full object-cover object-left-top"
          />
        </div>
      </div>
    </section>
  );
}
